import { inject, Injectable } from '@angular/core';
import { TNil } from '@app/app.interface';
import { ICtaTemplateApi } from '@campaign-form/form-fields/creators-and-ctas/cta-dialog.interface';
import { TDeliverable } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import {
  EExecutionType,
  TExecutionApi,
} from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { CustomValuesService } from '@core/services/custom-values.service';
import { EAnalyticsStatus, EMatchingType, EProcessStatus } from '@enums/campaign-analytics.enum';
import { ECampaignStatus, ECurrency, EPlatform, EPlatformFeature } from '@enums/campaign.enum';
import {
  IAnalyticsReferenceStateApi,
  ICampaignAnalyticsApi,
  ICampaignSocialContentApi,
  IContentCtaKpiStatusApi,
  ICreatorAnalyticsApi,
  ICreatorAnalyticsSliceApi,
  ICtaAnalyticsApi,
  ICtaAnalyticsDeliverables,
  ICtaKpiStatusableApi,
  ICtaKpiStatusApi,
  ICtaResultApi,
  IGlobalTrackingAnalytics,
  IPlannedCtaApi,
  IPlannedExecutionApi,
  IProcessableApi,
} from '@interfaces/campaign-analytics-api.interface';
import {
  IAnalyticsNode,
  IAnalyticsStatusable,
  ICampaignAnalytics,
  ICampaignSocialContent,
  ICreatorAnalytics,
  ICtaKpiStatus,
  ICtaKpiStatusable,
  ICtaResult,
  IPerformanceStatusable,
  IProcessable,
  IStatusableDisplayConfig,
} from '@interfaces/campaign-analytics.interface';
import { IGlobalTracking, TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { ICreator } from '@pages/creators/creators.interface';
import { isAfter, isBefore } from 'date-fns';
import { camelCase, get, sumBy } from 'lodash';
import { DateUtilitiesService } from './date-utilities.service';
import { SocialUtilitiesService } from './social-utilities.service';
import { SqlToAnalyticsUtilitiesService } from './sql-to-analytics-utilities.service';

@Injectable({
  providedIn: 'root',
})
export class CampaignAnalyticsUtilitiesService {
  private readonly customValuesService = inject(CustomValuesService);
  private readonly sqlDeliverableMapper = inject(SqlToAnalyticsUtilitiesService);
  private readonly dateUtils = inject(DateUtilitiesService);

  public getCtaDisplayNameForAnalyticsData(ctaResult: ICtaResult): string {
    const plannedExecutions = Array.from(ctaResult.executionResults.values()).filter(
      (execution) => execution.ctaExecutionId,
    );

    return plannedExecutions?.length > 1 ? (ctaResult.name ?? '') : (plannedExecutions?.[0]?.name ?? '');
  }

  public getDeliverablesAsSql(ctaAnalytics: ICtaAnalyticsApi): TDeliverable[] {
    return this.sqlDeliverableMapper.getDeliverablesInSqlFormat(ctaAnalytics);
  }

  public calculateAnalyticsStatusAndColor(
    status: EAnalyticsStatus | Set<EAnalyticsStatus>,
    target: Pick<ICtaKpiStatusable, 'status' | 'statusKpi' | 'ctaKpiStatus' | 'color'>,
    statusKpi?: Set<EAnalyticsStatus>,
  ): void {
    this.setStatusKpiAndColor(target, statusKpi);

    if (status instanceof Set) {
      this.setStatusAndColorWithStatusSet(status, target, statusKpi);
    } else {
      this.setAnalyticsStatusAndColor(status, target);
    }
  }

  public getStatusableDisplayConfig(
    status?: EAnalyticsStatus,
    entity?: IAnalyticsStatusable,
  ): IStatusableDisplayConfig {
    const currentStatus = entity?.status ?? status ?? EAnalyticsStatus.processing;

    switch (currentStatus) {
      case EAnalyticsStatus.ok:
        return {
          color: 'var(--shikenso-success-bright)',
          status: currentStatus,
        };
      case EAnalyticsStatus.failed:
        return {
          color: 'var(--shikenso-error-bright)',
          status: currentStatus,
        };
      case EAnalyticsStatus.problem:
        return {
          color: 'var(--shikenso-warning-base)',
          status: currentStatus,
        };
      default:
        return {
          color: 'var(--shikenso-info-base)',
          status: currentStatus,
        };
    }
  }

  public getExecutionStatus(analyticsNode: IAnalyticsNode): EMatchingType {
    if (
      analyticsNode?.content?.matchingType === EMatchingType.bonus ||
      analyticsNode?.content?.matchingType === EMatchingType.unrelated
    ) {
      return analyticsNode.content.matchingType;
    }

    const { content } = analyticsNode;
    const isMatched = content?.matchingType === EMatchingType.matched;

    if (!content || (!isMatched && (!content.socialContent || content.status === EAnalyticsStatus.failed))) {
      return this.getPlannedFailedExecutionStatus(
        analyticsNode.mergedCtaResult.status,
        analyticsNode.executionTime ?? undefined,
      );
    }

    const { socialContent } = content;
    const socialContentHasEndProperty = 'end' in (socialContent ?? {});

    if (isMatched || (socialContent?.executionTime && (!socialContentHasEndProperty || get(socialContent, 'end')))) {
      return EMatchingType.matched;
    }

    return EMatchingType.planned;
  }

  // no_expectations owes nothing (target 0), so calculateAnalyticsNodes never creates a planned node
  // for it. The briefing overview still lists those executions as optional, from these nodes - kept
  // out of the main node list so no count, table or timeline picks them up.
  public getNoExpectationsAnalyticsNodes(campaignAnalytics: ICampaignAnalytics): IAnalyticsNode[] {
    const analyticsNodes: IAnalyticsNode[] = [];

    campaignAnalytics.creators.forEach((creator) => {
      creator.plannedCtas.forEach((plannedCta) => {
        const baseAnalyticsNode = this.buildBasePlannedAnalyticsNode(campaignAnalytics, plannedCta);

        if (!baseAnalyticsNode) {
          return;
        }

        plannedCta.executions
          ?.filter((execution) => execution.executionType === EExecutionType.noExpectations)
          .forEach((execution) => {
            analyticsNodes.push({
              ...baseAnalyticsNode,
              id: this.buildAnalyticsNodeId(
                'optional',
                plannedCta.cta.creatorId,
                plannedCta.ctaId,
                execution.ctaExecutionId,
              ),
              isDateOnlyExecution: true,
              mergedCtaResult: campaignAnalytics.isReal
                ? this.getProcessableAndCtaKpiStatusable(plannedCta.cta)
                : this.getFallbackPlannedCtaResult(plannedCta.cta, execution),
              plannedExecution: execution,
            });
          });
      });
    });

    return analyticsNodes;
  }

  public getCampaignAnalyticsData(
    campaign: TCampaignApi,
    campaignAnalyticsApi: ICampaignAnalyticsApi | TNil,
  ): [ICampaignAnalytics, IAnalyticsNode[]] {
    campaign.campaignCreators = campaign.campaignCreators?.sort(
      (a, b) => a.creator?.name?.localeCompare(b.creator?.name ?? '') ?? 0,
    );

    if (!campaignAnalyticsApi) {
      const [emptyCampaignAnalytics, analyticsNodes] = this.calculateEmptyCampaignAnalytics(campaign);

      this.calculateTotalCountsForEmptyAnalytics(
        emptyCampaignAnalytics,
        this.getPlannedCtaAnalyticsNodes(emptyCampaignAnalytics, true),
      );

      return [emptyCampaignAnalytics, analyticsNodes];
    }

    return [this.calculateGlobalCampaignAnalytics(campaign, campaignAnalyticsApi), []];
  }

  public setAnalyticsStatusAndColor(
    status: EAnalyticsStatus,
    target: Pick<ICtaKpiStatusable, 'status' | 'ctaKpiStatus'>,
  ): void {
    if (target.status === status || target.status === EAnalyticsStatus.failed) {
      return;
    }

    if (status === EAnalyticsStatus.failed) {
      target.status = EAnalyticsStatus.failed;
      target.ctaKpiStatus.deliverableColor = 'var(--shikenso-error-bright)';

      return;
    }

    if (status === EAnalyticsStatus.problem) {
      target.status = EAnalyticsStatus.problem;
      target.ctaKpiStatus.deliverableColor = 'var(--shikenso-warning-base)';

      return;
    }

    if (status === EAnalyticsStatus.processing && target.status !== EAnalyticsStatus.problem) {
      target.status = EAnalyticsStatus.processing;
      target.ctaKpiStatus.deliverableColor = 'var(--shikenso-info-base)';

      return;
    }

    target.status = EAnalyticsStatus.ok;
    target.ctaKpiStatus.deliverableColor = 'var(--shikenso-success-bright)';
  }

  public calculateAnalyticsNodes(
    campaignAnalytics: ICampaignAnalytics,
    slices: ICreatorAnalyticsSliceApi[] | TNil,
    excludeUnrelatedMatchingTypes = false,
  ): IAnalyticsNode[] {
    const plannedCtaAnalyticsNodes = this.getPlannedCtaAnalyticsNodes(campaignAnalytics, true);
    const highestVersionSlices = this.getHighestVersionSlices(slices);
    const sliceAnalyticsNodes = this.getSliceAnalyticsNodes(
      highestVersionSlices,
      campaignAnalytics,
      excludeUnrelatedMatchingTypes,
    );

    return this.sortAnalyticsNodes([...plannedCtaAnalyticsNodes, ...sliceAnalyticsNodes]);
  }

  public getProcessableAndCtaKpiStatusable(
    analyticsData?: Pick<IProcessableApi, 'processStatus'> & ICtaKpiStatusableApi & IPerformanceStatusable,
  ): Pick<IProcessable, 'processStatus'> & ICtaKpiStatusable & IPerformanceStatusable {
    const processStatus = analyticsData?.processStatus ?? EProcessStatus.notStarted;
    const status = analyticsData?.status ?? EAnalyticsStatus.processing;
    const statusKpi = analyticsData?.statusKpi ?? EAnalyticsStatus.processing;
    const ctaKpiStatus = this.getCtaKpiStatus(analyticsData);
    const color = this.getAnalyticsStatusWithStatusSet(new Set([status, statusKpi]));
    const performanceStatus = analyticsData?.performanceStatus ?? {
      costs: 0,
    };

    return {
      color,
      ctaKpiStatus,
      performanceStatus,
      processStatus,
      status,
      statusKpi,
    };
  }

  public getAnalyticsStatusWithStatusSet(status: Set<EAnalyticsStatus>, or = false): EAnalyticsStatus {
    if (or) {
      return this.getAnalyticsStatusWithStatusSetOr(status);
    }

    if (status.size === 1 && status.has(EAnalyticsStatus.failed)) {
      return EAnalyticsStatus.failed;
    }

    if ((status.size === 1 && status.has(EAnalyticsStatus.processing)) || status.size === 0) {
      return EAnalyticsStatus.processing;
    }

    if (!status.has(EAnalyticsStatus.problem) && !status.has(EAnalyticsStatus.failed)) {
      return EAnalyticsStatus.ok;
    }

    return EAnalyticsStatus.problem;
  }

  private getAnalyticsStatusWithStatusSetOr(status: Set<EAnalyticsStatus>): EAnalyticsStatus {
    if (status.has(EAnalyticsStatus.ok)) {
      return EAnalyticsStatus.ok;
    }

    if (status.has(EAnalyticsStatus.problem)) {
      return EAnalyticsStatus.problem;
    }

    if (status.has(EAnalyticsStatus.failed)) {
      return EAnalyticsStatus.failed;
    }

    return EAnalyticsStatus.processing;
  }

  private getCtasMapOfSocialContent(
    socialContent: ICampaignSocialContentApi | TNil,
    excludeUnrelatedMatchingTypes: boolean,
  ): Map<string, ICtaResultApi> {
    if (!socialContent) {
      return new Map();
    }

    return (socialContent?.ctas ?? []).reduce((map: Map<string, ICtaResultApi>, cta: ICtaResultApi) => {
      if (excludeUnrelatedMatchingTypes && cta.matchingType === EMatchingType.unrelated) {
        return map;
      }

      map.set(cta.ctaId, {
        ...cta,
        ctaAnalytics: this.addGlobalTrackingAnalyticsToCtaAnalytics(
          cta.ctaAnalytics,
          socialContent.globalTrackings ? [socialContent.globalTrackings] : [],
        ),
      });

      return map;
    }, new Map<string, ICtaResultApi>());
  }

  private getPlannedCtaAnalyticsNodes(
    campaignAnalytics: ICampaignAnalytics,
    includeAlwaysExecutions = false,
  ): IAnalyticsNode[] {
    const analyticsNodes: IAnalyticsNode[] = [];

    campaignAnalytics.creators.forEach((creator) => {
      creator.plannedCtas.forEach((plannedCta) => {
        analyticsNodes.push(
          ...this.calculatePlannedCtaAnalyticsNodes(campaignAnalytics, plannedCta, includeAlwaysExecutions),
        );
      });
    });

    return analyticsNodes;
  }

  private setStatusAndColorWithStatusSet(
    status: Set<EAnalyticsStatus>,
    target: Pick<ICtaKpiStatusable, 'status' | 'statusKpi' | 'ctaKpiStatus' | 'color'>,
    statusKpi?: Set<EAnalyticsStatus>,
  ): void {
    const analyticsStatus = this.getAnalyticsStatusWithStatusSet(status);
    const ctaKpiStatusColor = this.getStatusableDisplayConfig(analyticsStatus).color;

    target.status = analyticsStatus;
    target.ctaKpiStatus.deliverableColor = ctaKpiStatusColor;

    this.setCtaKpiStatusColor(target, analyticsStatus, statusKpi);
  }

  private setStatusKpiAndColor(
    target: Pick<ICtaKpiStatusable, 'status' | 'statusKpi' | 'ctaKpiStatus'>,
    statusKpi?: Set<EAnalyticsStatus>,
  ): void {
    if (!statusKpi) {
      return;
    }

    const analyticsStatus = this.getAnalyticsStatusWithStatusSet(statusKpi);
    const ctaKpiStatusColor = this.getStatusableDisplayConfig(analyticsStatus).color;
    target.statusKpi = analyticsStatus;
    target.ctaKpiStatus.kpiColor = ctaKpiStatusColor;
  }

  private setCtaKpiStatusColor(
    target: Pick<ICtaKpiStatusable, 'status' | 'statusKpi' | 'ctaKpiStatus' | 'color'>,
    statusAnalytics: EAnalyticsStatus,
    statusKpi?: EAnalyticsStatus | Set<EAnalyticsStatus>,
  ): void {
    const statusKpiArray = statusKpi instanceof Set ? Array.from(statusKpi) : statusKpi ? [statusKpi] : [];
    const statusSet = new Set([statusAnalytics, ...statusKpiArray]);
    const mergedStatus = this.getAnalyticsStatusWithStatusSet(statusSet);
    const mergedStatusColor = this.getStatusableDisplayConfig(mergedStatus).color;

    target.color = mergedStatusColor;
  }

  private calculateEmptyCampaignAnalytics(campaign: TCampaignApi): [ICampaignAnalytics, IAnalyticsNode[]] {
    const campaignAnalytics: ICampaignAnalytics = {
      ...this.getProcessableAndCtaKpiStatusable({
        ctaKpiStatus: this.getCtaKpiStatus() as ICtaKpiStatusApi,
        performanceStatus: {
          costs: 0,
        },
        processStatus: campaign.status === ECampaignStatus.ended ? EProcessStatus.done : EProcessStatus.notStarted,
        status: EAnalyticsStatus.processing,
        statusKpi: EAnalyticsStatus.processing,
      }),
      campaignVersion: 0,
      creators: new Map(),
      end: this.dateUtils.parseDateTimeFromApi(campaign.originalEnd!),
      globalCurrency: campaign.currency ?? ECurrency.eur,
      globalTrackings: [],
      id: campaign.id,
      isReal: false,
      name: campaign.name,
      start: this.dateUtils.parseDateTimeFromApi(campaign.originalStart!),
      status: EAnalyticsStatus.processing,
      version: 0,
    };

    const analyticsNodes = this.calculateEmptyCreatorAnalytics(campaignAnalytics, campaign);

    return [campaignAnalytics, analyticsNodes];
  }

  private calculateEmptyCreatorAnalytics(
    campaignAnalytics: ICampaignAnalytics,
    campaign: TCampaignApi,
  ): IAnalyticsNode[] {
    campaignAnalytics.creators = new Map(
      campaign.campaignCreators?.map((campaignCreator): [string, ICreatorAnalytics] => {
        const { creator } = campaignCreator;
        const creatorId = creator?.id ?? campaignCreator.creatorId;
        const creatorPlatforms = creator?.channels?.map((channel) => camelCase(channel.platform) as EPlatform) ?? [];
        const usedPlatforms =
          creator?.channels?.map((channel) => ({
            platform: camelCase(channel.platform) as EPlatform,
            platformId: channel.id,
          })) ?? [];

        return [
          creatorId,
          {
            ...this.getProcessableAndCtaKpiStatusable(),
            campaignId: campaign.id,
            campaignVersion: 0,
            creatorId,
            deactivated: false,
            icon: creator?.icon ?? '',
            id: creatorId,
            isReal: false,
            lastProcessed: null,
            name: creator?.name ?? '',
            openSliceReferences: [],
            openSliceState: {
              ...this.getProcessableAndCtaKpiStatusable(),
              campaignVersion: 0,
              end: '',
              lastProcessed: null,
              matchedKpiAnalytics: [],
              start: '',
            } as IAnalyticsReferenceStateApi,
            plannedCtas: this.getEmptyPlannedCtasForCreator(campaign, creatorId, creatorPlatforms),
            slices: [],
            status: EAnalyticsStatus.processing,
            usedPlatforms,
            version: 0,
          },
        ];
      }) ?? [],
    );

    return this.calculateAnalyticsNodes(campaignAnalytics, []);
  }

  private getEmptyPlannedCtasForCreator(
    campaign: TCampaignApi,
    creatorId: string,
    creatorPlatforms: EPlatform[],
  ): Map<string, IPlannedCtaApi> {
    const plannedCtas = new Map<string, IPlannedCtaApi>();

    campaign.ctaTemplates?.forEach((ctaTemplate) => {
      if (!this.isCreatorCompatibleWithCtaTemplate(creatorId, creatorPlatforms, ctaTemplate)) {
        return;
      }

      const ctaTemplateWithCustomValues = this.customValuesService.getCtaWithCreatorCustomValues(
        ctaTemplate,
        creatorId,
      );
      const plannedCta = this.createEmptyPlannedCta(campaign, creatorId, ctaTemplateWithCustomValues);

      plannedCtas.set(`${plannedCta.ctaId}-${plannedCta.platformFeature}`, plannedCta);
    });

    return plannedCtas;
  }

  private isCreatorCompatibleWithCtaTemplate(
    creatorId: string,
    creatorPlatforms: EPlatform[],
    ctaTemplate: ICtaTemplateApi,
  ): boolean {
    if (
      (!ctaTemplate.isAllCreators &&
        !ctaTemplate.ctaCreators?.some((ctaCreator) => ctaCreator.creatorId === creatorId)) ||
      (ctaTemplate.isAllCreators &&
        !SocialUtilitiesService.isPlatformCompatible(ctaTemplate.platformFeature, creatorPlatforms))
    ) {
      return false;
    }

    return true;
  }

  private createEmptyPlannedCta(
    campaign: TCampaignApi,
    creatorId: string,
    ctaTemplateWithCustomValues: ICtaTemplateApi,
  ): IPlannedCtaApi {
    const emptyCtaAnalytics = this.getEmptyCtaAnalytics(ctaTemplateWithCustomValues, campaign.globalTracking);
    const expectedKpis =
      ctaTemplateWithCustomValues.executions?.reduce((totalExpectedKpis, execution) => {
        return totalExpectedKpis + (execution.expectedKpis?.length ?? 0);
      }, 0) ?? 0;
    const plannedExecutions =
      ctaTemplateWithCustomValues.executions?.map((execution) => {
        return this.calculateEmptyPlannedExecution(execution, ctaTemplateWithCustomValues);
      }) ?? [];

    return {
      cta: {
        campaignId: campaign.id,
        creatorId,
        ctaAnalytics: emptyCtaAnalytics,
        ctaId: ctaTemplateWithCustomValues.id,
        ctaKpiStatus: {
          ...this.getContentCtaKpiStatus(),
          currentBonusDeliverables: 0,
          currentFailedDeliverables: 0,
          currentFailedKpis: 0,
          currentOkDeliverables: 0,
          currentOkKpis: 0,
          currentProblemDeliverables: 0,
          currentProblemKpis: 0,
          currentProcessingDeliverables: 0,
          currentProcessingKpis: 0,
          expectedContents: sumBy(plannedExecutions, (execution) => execution.executionCountTarget ?? 1) ?? 0,
          expectedDeliverables: emptyCtaAnalytics.totalDeliverables,
          expectedKpis,
          totalDeliverables: emptyCtaAnalytics.totalDeliverables,
        },
        matchingType: EMatchingType.matched,
        name: ctaTemplateWithCustomValues.name ?? '',
        performanceStatus: {
          costs: 0,
        },
        processStatus: EProcessStatus.notStarted,
        status: EAnalyticsStatus.processing,
        statusKpi: EAnalyticsStatus.processing,
        version: 1,
      },
      ctaId: ctaTemplateWithCustomValues.id,
      executions: plannedExecutions,
      platformFeature: camelCase(ctaTemplateWithCustomValues.platformFeature) as EPlatformFeature,
      version: 1,
    };
  }

  private calculateEmptyPlannedExecution(execution: TExecutionApi, ctaTemplate: ICtaTemplateApi): IPlannedExecutionApi {
    return {
      ctaExecutionId: execution.id,
      ctaId: ctaTemplate.id,
      executionCountCurrent: 0,
      executionCountTarget: execution.executionCountTarget ?? 1,
      executionType: execution.executionType ?? EExecutionType.undesignated,
      expectedKpis:
        execution.expectedKpis?.map((expectedKpi) => ({
          expectedKpiId: expectedKpi.id,
          expectedValue: expectedKpi.expectedValue ?? 0,
          kpi: expectedKpi.kpi,
        })) ?? [],
      plannedDuration: execution.duration,
      plannedEnd: execution.executionEndDate ?? null,
      plannedStart: execution.executionStartDate ?? null,
      plannedStartDate: execution.executionDate,
      plannedStartTime: execution.executionTime,
      platformFeature: camelCase(ctaTemplate.platformFeature) as EPlatformFeature,
      version: 1,
    };
  }

  private calculateEmptyMergedCtaResult(
    ctaTemplateWithCustomValues: ICtaTemplateApi,
    execution: TExecutionApi,
  ): ICtaKpiStatusable {
    const executionTime = execution.executionTime ?? execution.executionDate ?? null;
    const executionTimePassed = executionTime ? isAfter(new Date(), executionTime) : false;
    const ctaKpiStatus = this.getCtaKpiStatus({
      ctaKpiStatus: {
        expectedDeliverables: this.getTotalDeliverableCountForCta(ctaTemplateWithCustomValues),
        expectedKpis: execution.expectedKpis?.length ?? 0,
      } as ICtaKpiStatusApi,
      status: EAnalyticsStatus.processing,
      statusKpi: EAnalyticsStatus.processing,
    });

    return this.getProcessableAndCtaKpiStatusable({
      ctaKpiStatus: ctaKpiStatus as ICtaKpiStatusApi,
      performanceStatus: {
        costs: 0,
      },
      processStatus: EProcessStatus.notStarted,
      status: executionTimePassed ? EAnalyticsStatus.failed : EAnalyticsStatus.processing,
      statusKpi: executionTimePassed ? EAnalyticsStatus.failed : EAnalyticsStatus.processing,
    });
  }

  private getEmptyCtaAnalytics(
    ctaTemplateWithCustomValues: ICtaTemplateApi,
    globalTracking?: IGlobalTracking,
  ): ICtaAnalyticsApi {
    const partialCtaAnalytics: Pick<
      ICtaAnalyticsApi,
      'currentDeliverables' | 'processStatus' | 'status' | 'totalDeliverables'
    > = {
      currentDeliverables: 0,
      processStatus: EProcessStatus.notStarted,
      status: EAnalyticsStatus.processing,
      totalDeliverables: this.getTotalDeliverableCountForCta(ctaTemplateWithCustomValues),
    };

    return {
      ...partialCtaAnalytics,
      matchingScore: 1,
      ...this.mapCtaTemplateDeliverablesToAnalytics(ctaTemplateWithCustomValues, globalTracking),
    };
  }

  private mapCtaTemplateDeliverablesToAnalytics(
    ctaTemplateWithCustomValues: ICtaTemplateApi,
    globalTracking?: IGlobalTracking,
  ): ICtaAnalyticsDeliverables {
    return {
      audioTrackings: this.sqlDeliverableMapper.mapAudioTrackingsToAnalytics(
        ctaTemplateWithCustomValues,
        globalTracking,
      ),
      captions: this.sqlDeliverableMapper.mapCaptionsToAnalytics(ctaTemplateWithCustomValues),
      chatbot: this.sqlDeliverableMapper.mapChatbotsToAnalytics(ctaTemplateWithCustomValues),
      chatbotMultis: this.sqlDeliverableMapper.mapChatbotMultisToAnalytics(ctaTemplateWithCustomValues),
      contents: this.sqlDeliverableMapper.mapContentsToAnalytics(ctaTemplateWithCustomValues),
      customs: this.sqlDeliverableMapper.mapCustomsToAnalytics(ctaTemplateWithCustomValues),
      descriptions: this.sqlDeliverableMapper.mapDescriptionsToAnalytics(ctaTemplateWithCustomValues),
      gamePlay: this.sqlDeliverableMapper.mapGamePlaysToAnalytics(ctaTemplateWithCustomValues),
      hashtags: this.sqlDeliverableMapper.mapHashTagsToAnalytics(ctaTemplateWithCustomValues),
      mentions: this.sqlDeliverableMapper.mapMentionsToAnalytics(ctaTemplateWithCustomValues),
      messages: this.sqlDeliverableMapper.mapMessagesToAnalytics(ctaTemplateWithCustomValues),
      productPlacements: this.sqlDeliverableMapper.mapProductPlacementsToAnalytics(ctaTemplateWithCustomValues),
      showcases: this.sqlDeliverableMapper.mapShowcasesToAnalytics(ctaTemplateWithCustomValues),
      talkingPointAis: this.sqlDeliverableMapper.mapTalkingPointAisToAnalytics(ctaTemplateWithCustomValues),
      talkingPoints: this.sqlDeliverableMapper.mapTalkingPointsToAnalytics(ctaTemplateWithCustomValues),
      textTrackings: this.sqlDeliverableMapper.mapTextTrackingsToAnalytics(ctaTemplateWithCustomValues, globalTracking),
      titles: this.sqlDeliverableMapper.mapTitlesToAnalytics(ctaTemplateWithCustomValues),
      visuals: this.sqlDeliverableMapper.mapVisualsToAnalytics(ctaTemplateWithCustomValues),
    };
  }

  private calculateGlobalCampaignAnalytics(
    campaign: TCampaignApi,
    campaignAnalyticsApi: ICampaignAnalyticsApi,
  ): ICampaignAnalytics {
    const campaignAnalytics: ICampaignAnalytics = {
      ...campaignAnalyticsApi,
      ...this.getProcessableAndCtaKpiStatusable(campaignAnalyticsApi),
      creators: new Map(),
      end: this.dateUtils.parseDateTimeFromApi(campaign.originalEnd!),
      id: campaign.id,
      isReal: true,
      lastProcessed: campaignAnalyticsApi.lastProcessed
        ? this.dateUtils.parseDateTimeFromApi(campaignAnalyticsApi.lastProcessed)
        : null,
      name: campaign.name,
      start: this.dateUtils.parseDateTimeFromApi(campaign.originalStart!),
    };

    if (campaignAnalyticsApi.creatorAnalytics.length) {
      this.calculateCreatorAnalytics(campaignAnalytics, campaign, campaignAnalyticsApi);
    }

    return campaignAnalytics;
  }

  private calculateCreatorAnalytics(
    campaignAnalytics: ICampaignAnalytics,
    campaign: TCampaignApi,
    campaignAnalyticsApi: ICampaignAnalyticsApi,
  ): void {
    const campaignAnalyticsCreators: ICreatorAnalytics[] = [];

    campaign.campaignCreators?.forEach((campaignCreator) => {
      const { creator } = campaignCreator;
      const creatorId = creator?.id ?? campaignCreator.creatorId;
      const creatorAnalytics = campaignAnalyticsApi.creatorAnalytics.find((ca) => ca.creatorId === creatorId);

      if (!creatorAnalytics) {
        return;
      }

      const plannedCtas = this.getPlannedCtasMapForCreator(creatorAnalytics);
      const campaignAnalyticsCreator: ICreatorAnalytics = {
        ...creatorAnalytics,
        ...this.getProcessableAndCtaKpiStatusable(creatorAnalytics),
        icon: creator?.icon ?? '',
        id: creatorId,
        isReal: true,
        lastProcessed: creatorAnalytics.lastProcessed
          ? this.dateUtils.parseDateTimeFromApi(creatorAnalytics.lastProcessed)
          : null,
        name: creator?.name ?? '',
        plannedCtas,
        slices: [],
      };

      campaignAnalyticsCreators.push(campaignAnalyticsCreator);
    });

    campaignAnalytics.creators = new Map(campaignAnalyticsCreators.map((creator) => [creator.id, creator]));
  }

  /**
   * Builds a stable IAnalyticsNode id from natural keys instead of a random uuid, so the same
   * node keeps the same id across background refreshes (recomputing analytics regenerates
   * IAnalyticsNode instances, and code that re-looks-up a node by id after a refresh, e.g. to
   * reopen a dialog, would otherwise silently fail to find it).
   */
  private buildAnalyticsNodeId(...parts: Array<string | number | null | undefined>): string {
    return parts.filter((part) => part !== null && part !== undefined && part !== '').join('_');
  }

  private buildBasePlannedAnalyticsNode(
    campaignAnalytics: ICampaignAnalytics,
    plannedCta: IPlannedCtaApi,
  ): Omit<IAnalyticsNode, 'id' | 'mergedCtaResult'> | null {
    const { cta } = plannedCta;
    const creator = campaignAnalytics.creators.get(cta.creatorId);

    if (!creator) {
      return null;
    }

    return {
      campaignId: cta.campaignId,
      content: null,
      creator: { icon: creator.icon ?? null, id: creator.id, name: creator.name },
      creatorId: cta.creatorId,
      ctaIds: new Set([plannedCta.ctaId]),
      emptyCtaAnalytics: this.addGlobalTrackingAnalyticsToCtaAnalytics(
        cta.ctaAnalytics,
        campaignAnalytics.globalTrackings,
      ),
      executionStatus: EMatchingType.planned,
      executionTime: null,
      isLive: false,
      isReal: campaignAnalytics.isReal,
      nonExecutedCtaId: plannedCta.ctaId,
      nonExecutedCtaName: cta.name ?? null,
      plannedExecution: null,
      platformFeature: plannedCta.platformFeature,
    };
  }

  private calculatePlannedCtaAnalyticsNodes(
    campaignAnalytics: ICampaignAnalytics,
    plannedCta: IPlannedCtaApi,
    includeAlwaysExecutions = false,
  ): IAnalyticsNode[] {
    const { cta } = plannedCta;
    const baseAnalyticsNode = this.buildBasePlannedAnalyticsNode(campaignAnalytics, plannedCta);

    if (!baseAnalyticsNode) {
      return [];
    }

    const analyticsNodes: IAnalyticsNode[] = [];

    plannedCta.executions?.forEach((execution) => {
      if (!includeAlwaysExecutions && execution.executionType === EExecutionType.always) {
        return;
      }

      const executionCountTarget = execution.executionCountTarget ?? 1;
      const executionCountCurrent = execution.executionCountCurrent ?? 1;

      for (let i = 0; i < executionCountTarget - executionCountCurrent; i++) {
        const mergedCtaResult = campaignAnalytics.isReal
          ? this.getProcessableAndCtaKpiStatusable(cta)
          : this.getFallbackPlannedCtaResult(cta, execution);
        const analyticsNode: IAnalyticsNode = {
          ...baseAnalyticsNode,
          executionTime: execution.plannedStartTime ?? execution.plannedStartDate ?? null,
          id: this.buildAnalyticsNodeId('planned', cta.creatorId, plannedCta.ctaId, execution.ctaExecutionId, i),
          isDateOnlyExecution: !execution.plannedStartTime,
          mergedCtaResult,
          plannedExecution: execution,
        };

        if (analyticsNode.executionTime) {
          analyticsNode.executionStatus = this.getExecutionStatus(analyticsNode);
        } else if (this.isDatelessPlannedExecutionMissing(execution, campaignAnalytics.end)) {
          analyticsNode.executionStatus = EMatchingType.missing;
        }

        analyticsNodes.push(analyticsNode);
      }
    });

    return analyticsNodes;
  }

  // A time_frame is due at the END of its window (anywhere inside is still on time); no_expectations
  // owes nothing and is never missing; every other dateless type is due when the campaign ends.
  private isDatelessPlannedExecutionMissing(execution: IPlannedExecutionApi, campaignEnd: Date): boolean {
    if (execution.executionType === EExecutionType.noExpectations) {
      return false;
    }

    const now = new Date();
    const isTimeFrameOver =
      execution.executionType === EExecutionType.timeFrame &&
      !!execution.plannedEnd &&
      isBefore(this.dateUtils.parseDateTimeFromApi(execution.plannedEnd), now);

    return isTimeFrameOver || isBefore(campaignEnd, now);
  }

  private getFallbackPlannedCtaResult(cta: ICtaResultApi, execution: IPlannedExecutionApi): ICtaKpiStatusable {
    // plannedEnd is only set for time_frame, whose deadline is the end of its window.
    const executionTime = execution.plannedStartTime ?? execution.plannedStartDate ?? execution.plannedEnd ?? null;
    const executionTimePassed = executionTime
      ? isAfter(new Date(), this.dateUtils.parseDateTimeFromApi(executionTime))
      : false;

    return this.getProcessableAndCtaKpiStatusable({
      ctaKpiStatus: {
        currentBonusDeliverables: 0,
        currentFailedDeliverables: 0,
        currentFailedKpis: 0,
        currentOkDeliverables: 0,
        currentOkKpis: 0,
        currentProblemDeliverables: 0,
        currentProblemKpis: 0,
        currentProcessingDeliverables: 0,
        currentProcessingKpis: 0,
        expectedDeliverables: cta.ctaAnalytics.totalDeliverables,
        expectedKpis: execution.expectedKpis.length,
        totalDeliverables: cta.ctaAnalytics.totalDeliverables,
      } as ICtaKpiStatusApi,
      performanceStatus: {
        costs: 0,
      },
      processStatus: EProcessStatus.notStarted,
      status: executionTimePassed ? EAnalyticsStatus.failed : EAnalyticsStatus.processing,
      statusKpi: executionTimePassed ? EAnalyticsStatus.failed : EAnalyticsStatus.processing,
    });
  }

  private getSliceAnalyticsNodes(
    slices: ICreatorAnalyticsSliceApi[] | TNil,
    campaignAnalytics: ICampaignAnalytics,
    excludeUnrelatedMatchingTypes: boolean,
  ): IAnalyticsNode[] {
    if (!slices) {
      return [];
    }

    const analyticsNodes: IAnalyticsNode[] = [];

    slices.forEach((slice) => {
      const creatorAnalytics = campaignAnalytics.creators.get(slice.creatorId);
      const creator: ICreator = {
        icon: creatorAnalytics?.icon ?? null,
        id: creatorAnalytics?.id ?? '',
        name: creatorAnalytics?.name ?? '',
      };

      analyticsNodes.push(...this.calculateSliceAnalyticsNodes(slice, creator, excludeUnrelatedMatchingTypes));
    });

    return analyticsNodes;
  }

  private calculateSliceAnalyticsNodes(
    slice: ICreatorAnalyticsSliceApi,
    creator: ICreator,
    excludeUnrelatedMatchingTypes: boolean,
  ): IAnalyticsNode[] {
    const analyticsNodes: IAnalyticsNode[] = [];

    // Defensive: every cross-reference elsewhere in this file guards a possibly-missing array
    // (Map.get() + a null check, `?.`, or `??`) - this one didn't. Nothing in the current demo
    // fixture is missing socialContents (verified directly against the recorded data), but a
    // future recording or a real API response with a slice that genuinely has none shouldn't be
    // able to throw synchronously here and get past callers as unhandled (see
    // CampaignDetailsStore.fetchDeliverablesTab's handleTabProcessingError for the other half of
    // this defense - this guard just avoids relying on that safety net for something this cheap
    // to prevent outright).
    slice.socialContents?.forEach((content) => {
      if (excludeUnrelatedMatchingTypes && content.matchingType === EMatchingType.unrelated) {
        return;
      }

      analyticsNodes.push(
        this.calculateSocialContentAnalyticsNodes(slice, creator, content, excludeUnrelatedMatchingTypes),
      );
    });

    return analyticsNodes;
  }

  private calculateSocialContentAnalyticsNodes(
    slice: ICreatorAnalyticsSliceApi,
    creator: ICreator,
    content: ICampaignSocialContentApi,
    excludeUnrelatedMatchingTypes: boolean,
  ): IAnalyticsNode {
    const formattedContent: ICampaignSocialContent = {
      ...content,
      ctas: this.getCtasMapOfSocialContent(content, excludeUnrelatedMatchingTypes),
    };
    const mergedCtaResult = this.calculateMergedCtaResult(formattedContent);

    const analyticsNode: IAnalyticsNode = {
      campaignId: slice.campaignId,
      content: formattedContent,
      creator,
      creatorId: slice.creatorId,
      ctaIds: new Set(formattedContent.ctas.keys()),
      emptyCtaAnalytics: null,
      executionStatus: EMatchingType.planned,
      executionTime: content.socialContent?.executionTime ?? null,
      id: this.buildAnalyticsNodeId('content', creator.id, content.id),
      isLive: false,
      isReal: true,
      mergedCtaResult,
      nonExecutedCtaId: null,
      nonExecutedCtaName: null,
      plannedExecution: null,
      platformFeature: camelCase(slice.platformFeature) as EPlatformFeature,
    };

    analyticsNode.isLive = SocialUtilitiesService.isSocialLive(content.socialContent);
    analyticsNode.executionStatus = this.getExecutionStatus(analyticsNode);

    return analyticsNode;
  }

  private calculateMergedCtaResult(content: ICampaignSocialContent): ICtaKpiStatusable {
    const ctas = Array.from(content.ctas.values());
    const mergedCtaResult: ICtaKpiStatusable = {
      color: 'var(--shikenso-info-base)',
      ctaKpiStatus: {
        ...content.ctaKpiStatus,
        deliverableColor: 'var(--shikenso-info-base)',
        kpiColor: 'var(--shikenso-info-base)',
        lastProcessed: content.lastProcessed ? this.dateUtils.parseDateTimeFromApi(content.lastProcessed) : null,
      },
      status: content.status,
      statusKpi: content.statusKpi,
    };

    const statusSet = new Set(ctas.map((cta) => cta.status));

    this.calculateAnalyticsStatusAndColor(statusSet, mergedCtaResult, new Set([mergedCtaResult.statusKpi]));

    return mergedCtaResult;
  }

  private getPlannedCtasMapForCreator(creatorAnalytics: ICreatorAnalyticsApi | undefined): Map<string, IPlannedCtaApi> {
    if (!creatorAnalytics) {
      return new Map();
    }

    return this.getHighestVersionPlannedCtas(creatorAnalytics.plannedCtas);
  }

  private getHighestVersionPlannedCtas(plannedCtas: IPlannedCtaApi[]): Map<string, IPlannedCtaApi> {
    const uniqueHighestVersionCtas = new Map<string, IPlannedCtaApi>();

    for (const plannedCta of plannedCtas) {
      plannedCta.platformFeature = camelCase(plannedCta.platformFeature) as EPlatformFeature;
      const compositeKey = `${plannedCta.ctaId}-${plannedCta.platformFeature}`;
      const existingResult = uniqueHighestVersionCtas.get(compositeKey);

      if (!existingResult || (plannedCta.version ?? 0) > (existingResult.version ?? 0)) {
        uniqueHighestVersionCtas.set(compositeKey, plannedCta);
      }
    }

    return uniqueHighestVersionCtas;
  }

  private getHighestVersionSlices(slices: ICreatorAnalyticsSliceApi[] | TNil): ICreatorAnalyticsSliceApi[] {
    if (!slices) {
      return [];
    }

    const uniqueHighestVersionSlices = new Map<string, ICreatorAnalyticsSliceApi>();

    for (const slice of slices) {
      const compositeKey = `${slice.creatorId}-${slice.start}-${slice.end}-${slice.platformFeature}`;
      const existingSlice = uniqueHighestVersionSlices.get(compositeKey);

      if (!existingSlice || (slice.version ?? 0) > (existingSlice.version ?? 0)) {
        uniqueHighestVersionSlices.set(compositeKey, slice);
      }
    }

    return Array.from(uniqueHighestVersionSlices.values());
  }

  private getCtaKpiStatus(ctaKpiStatusable?: ICtaKpiStatusableApi | ICtaKpiStatusable): ICtaKpiStatus {
    const deliverableColor = this.getStatusableDisplayConfig(ctaKpiStatusable?.status).color;
    const kpiColor = this.getStatusableDisplayConfig(ctaKpiStatusable?.statusKpi).color;
    const ctaKpiStatus = ctaKpiStatusable?.ctaKpiStatus;

    return {
      ...this.getContentCtaKpiStatus(ctaKpiStatus),
      currentBonusDeliverables: ctaKpiStatus?.currentBonusDeliverables ?? 0,
      currentFailedDeliverables: ctaKpiStatus?.currentFailedDeliverables ?? 0,
      currentFailedKpis: ctaKpiStatus?.currentFailedKpis ?? 0,
      currentOkDeliverables: ctaKpiStatus?.currentOkDeliverables ?? 0,
      currentOkKpis: ctaKpiStatus?.currentOkKpis ?? 0,
      currentProblemDeliverables: ctaKpiStatus?.currentProblemDeliverables ?? 0,
      currentProblemKpis: ctaKpiStatus?.currentProblemKpis ?? 0,
      currentProcessingDeliverables: ctaKpiStatus?.currentProcessingDeliverables ?? 0,
      currentProcessingKpis: ctaKpiStatus?.currentProcessingKpis ?? 0,
      deliverableColor,
      expectedDeliverables: ctaKpiStatus?.expectedDeliverables ?? 0,
      expectedKpis: ctaKpiStatus?.expectedKpis ?? 0,
      kpiColor,
      totalDeliverables: ctaKpiStatus?.totalDeliverables ?? 0,
    };
  }

  private getContentCtaKpiStatus(ctaKpiStatus?: ICtaKpiStatus | ICtaKpiStatusApi | TNil): IContentCtaKpiStatusApi {
    return {
      currentBonusContents: ctaKpiStatus?.currentBonusContents ?? 0,
      currentFailedContents: ctaKpiStatus?.currentFailedContents ?? 0,
      currentMatchedContents: ctaKpiStatus?.currentMatchedContents ?? 0,
      currentMissingContents: ctaKpiStatus?.currentMissingContents ?? 0,
      currentOkContents: ctaKpiStatus?.currentOkContents ?? 0,
      currentProblemContents: ctaKpiStatus?.currentProblemContents ?? 0,
      currentProcessingContents: ctaKpiStatus?.currentProcessingContents ?? 0,
      currentUnrelatedContents: ctaKpiStatus?.currentUnrelatedContents ?? 0,
      expectedContents: ctaKpiStatus?.expectedContents ?? 0,
    };
  }

  private getTotalDeliverableCountForCta(cta: ICtaTemplateApi): number {
    return (
      this.getLength(cta.titles?.length) +
      this.getLength(cta.talkingPoints?.length) +
      this.getLength(cta.visuals?.length) +
      this.getLength(cta.chatbots?.length) +
      this.getLength(cta.productPlacements?.length) +
      this.getLength(cta.gamePlays?.length) +
      this.getLength(cta.showcases?.length) +
      this.getLength(cta.textTrackings?.length) +
      this.getLength(cta.audioTrackings?.length) +
      this.getLength(cta.captions?.length) +
      this.getLength(cta.hashTags?.length) +
      this.getLength(cta.mentions?.length) +
      this.getLength(cta.messages?.length) +
      this.getLength(cta.descriptions?.length) +
      this.getLength(cta.contents?.length) +
      this.getLength(cta.customs?.length)
    );
  }

  private getLength(length: number | undefined): number {
    return length ?? 0;
  }

  private calculateTotalCountsForEmptyAnalytics(
    campaignAnalytics: ICampaignAnalytics,
    analyticsNodes: IAnalyticsNode[],
  ): void {
    let expectedCampaignDeliverables = 0;
    let expectedCampaignKpis = 0;

    analyticsNodes.forEach((node) => {
      expectedCampaignDeliverables += node.mergedCtaResult.ctaKpiStatus.expectedDeliverables;
      expectedCampaignKpis += node.mergedCtaResult.ctaKpiStatus.expectedKpis;

      const creatorAnalytics = campaignAnalytics.creators.get(node.creatorId);

      if (!creatorAnalytics) {
        return;
      }

      creatorAnalytics.ctaKpiStatus.expectedDeliverables += node.mergedCtaResult.ctaKpiStatus.expectedDeliverables;
      creatorAnalytics.ctaKpiStatus.expectedKpis += node.mergedCtaResult.ctaKpiStatus.expectedKpis;
    });

    campaignAnalytics.ctaKpiStatus.expectedDeliverables = expectedCampaignDeliverables;
    campaignAnalytics.ctaKpiStatus.expectedKpis = expectedCampaignKpis;
  }

  private getPlannedFailedExecutionStatus(analyticsStatus: EAnalyticsStatus, executionTime?: string): EMatchingType {
    let plannedDayPassed = false;

    if (executionTime) {
      const executionDay = this.dateUtils.removeHoursAndRest(this.dateUtils.parseDateTimeFromApi(executionTime));
      const today = this.dateUtils.removeHoursAndRest(new Date());

      plannedDayPassed = isBefore(executionDay, today);
    }

    return analyticsStatus === EAnalyticsStatus.failed || plannedDayPassed
      ? EMatchingType.missing
      : EMatchingType.planned;
  }

  private sortAnalyticsNodes(analyticsNodes: IAnalyticsNode[]): IAnalyticsNode[] {
    return analyticsNodes.sort((a, b) => {
      const aStartTime = a.content?.socialContent?.executionTime ?? a.plannedExecution?.plannedStartTime;
      const bStartTime = b.content?.socialContent?.executionTime ?? b.plannedExecution?.plannedStartTime;

      // Items with no date first
      if (!aStartTime && bStartTime) {
        return -1;
      }

      if (aStartTime && !bStartTime) {
        return 1;
      }

      if (!aStartTime && !bStartTime) {
        return 0;
      }

      // Both have dates: sort by newest first
      return (
        new Date(bStartTime as string | number | Date).getTime() -
        new Date(aStartTime as string | number | Date).getTime()
      );
    });
  }

  private addGlobalTrackingAnalyticsToCtaAnalytics(
    ctaAnalytics: ICtaAnalyticsApi,
    globalTrackingAnalytics?: IGlobalTrackingAnalytics[],
  ): ICtaAnalyticsApi {
    if (!globalTrackingAnalytics) {
      return ctaAnalytics;
    }

    const globalAudioTrackings = [];
    const globalTextTrackings = [];

    for (const globalTracking of globalTrackingAnalytics) {
      globalAudioTrackings.push(...globalTracking.audioTrackings);
      globalTextTrackings.push(...globalTracking.textTrackings);
    }

    return {
      ...ctaAnalytics,
      audioTrackings: [...ctaAnalytics.audioTrackings, ...globalAudioTrackings],
      textTrackings: [...ctaAnalytics.textTrackings, ...globalTextTrackings],
    };
  }
}
